import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export type SuggestionType = "skill" | "industry";
export type SuggestionStatus = "pending" | "approved" | "rejected";

export interface TaxonomySuggestion {
  id: string;
  suggestion_type: SuggestionType;
  suggested_label: string;
  suggested_description: string | null;
  suggested_by: string;
  status: SuggestionStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  approved_item_id: string | null;
  final_label: string | null;
  created_at: string;
  // Joined data
  suggester_profile?: {
    full_name: string;
  } | null;
}

// Hook for users to view their own suggestions
export function useMyTaxonomySuggestions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["my_taxonomy_suggestions", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("taxonomy_suggestions")
        .select("*")
        .eq("suggested_by", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as TaxonomySuggestion[];
    },
    enabled: !!user?.id,
  });
}

// Hook for admins to view all suggestions
export function useAllTaxonomySuggestions(filters?: {
  type?: SuggestionType;
  status?: SuggestionStatus;
}) {
  return useQuery({
    queryKey: ["all_taxonomy_suggestions", filters],
    queryFn: async () => {
      let query = supabase
        .from("taxonomy_suggestions")
        .select("*")
        .order("created_at", { ascending: false });

      if (filters?.type) {
        query = query.eq("suggestion_type", filters.type);
      }
      if (filters?.status) {
        query = query.eq("status", filters.status);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch profiles separately since there's no FK
      const userIds = [...new Set((data || []).map(s => s.suggested_by))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      return (data || []).map(s => ({
        ...s,
        suggester_profile: profileMap.get(s.suggested_by) || null,
      })) as TaxonomySuggestion[];
    },
  });
}

// Hook for creating a new suggestion
export function useCreateTaxonomySuggestion() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      type,
      label,
      description,
    }: {
      type: SuggestionType;
      label: string;
      description?: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("taxonomy_suggestions")
        .insert({
          suggestion_type: type,
          suggested_label: label.trim(),
          suggested_description: description?.trim() || null,
          suggested_by: user.id,
        })
        .select()
        .single();

      if (error) {
        // Parse custom error messages from triggers
        if (error.message.includes("ALREADY_EXISTS:")) {
          throw new Error(error.message.split("ALREADY_EXISTS:")[1]);
        }
        if (error.message.includes("PENDING_EXISTS:")) {
          throw new Error(error.message.split("PENDING_EXISTS:")[1]);
        }
        if (error.message.includes("Rate limit exceeded")) {
          throw new Error("You can only submit 5 suggestions per 24 hours. Please try again later.");
        }
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my_taxonomy_suggestions"] });
      toast.success("Thanks — your suggestion is pending review.");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// Hook for admins to approve/reject suggestions
export function useReviewTaxonomySuggestion() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      suggestionId,
      action,
      finalLabel,
      rejectionReason,
    }: {
      suggestionId: string;
      action: "approve" | "reject";
      finalLabel?: string;
      rejectionReason?: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      // First, get the suggestion details
      const { data: suggestion, error: fetchError } = await supabase
        .from("taxonomy_suggestions")
        .select("*")
        .eq("id", suggestionId)
        .single();

      if (fetchError) throw fetchError;

      if (action === "approve") {
        const labelToUse = finalLabel?.trim() || suggestion.suggested_label;
        let approvedItemId: string | null = null;

        // Insert into the appropriate table
        if (suggestion.suggestion_type === "skill") {
          const { data: newSkill, error: insertError } = await supabase
            .from("skills")
            .insert({ name: labelToUse })
            .select()
            .single();

          if (insertError) {
            if (insertError.message.includes("duplicate")) {
              throw new Error("A skill with this name already exists");
            }
            throw insertError;
          }
          approvedItemId = newSkill.id;
        } else {
          const { data: newIndustry, error: insertError } = await supabase
            .from("industries")
            .insert({ name: labelToUse })
            .select()
            .single();

          if (insertError) {
            if (insertError.message.includes("duplicate")) {
              throw new Error("An industry with this name already exists");
            }
            throw insertError;
          }
          approvedItemId = newIndustry.id;
        }

        // Update the suggestion status
        const { error: updateError } = await supabase
          .from("taxonomy_suggestions")
          .update({
            status: "approved",
            reviewed_by: user.id,
            reviewed_at: new Date().toISOString(),
            approved_item_id: approvedItemId,
            final_label: labelToUse,
          })
          .eq("id", suggestionId);

        if (updateError) throw updateError;
      } else {
        // Reject
        const { error: updateError } = await supabase
          .from("taxonomy_suggestions")
          .update({
            status: "rejected",
            reviewed_by: user.id,
            reviewed_at: new Date().toISOString(),
            rejection_reason: rejectionReason || null,
          })
          .eq("id", suggestionId);

        if (updateError) throw updateError;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["all_taxonomy_suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["my_taxonomy_suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      queryClient.invalidateQueries({ queryKey: ["industries"] });
      toast.success(
        variables.action === "approve"
          ? "Suggestion approved and added to the official list"
          : "Suggestion rejected"
      );
    },
    onError: (error: Error) => {
      toast.error("Failed to review suggestion: " + error.message);
    },
  });
}
